import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, MapPin, Clock, Shield, Zap, AlertTriangle, CircleDot, Wind, Settings } from 'lucide-react';
import { PHONES, CONTACT, DISTRICTS } from '../content';
import { DISTRICT_PAGES } from '../data/districts';
import FinalCTA from '../components/FinalCTA';
import LiveBadge from '../components/LiveBadge';
import { SEOHead } from '../components/SEOHead';

const WA_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M5.077 18.828l.532-1.869A8.85 8.85 0 0 1 3.2 12C3.2 7.134 7.134 3.2 12 3.2c4.866 0 8.8 3.934 8.8 8.8 0 4.866-3.934 8.8-8.8 8.8a8.845 8.845 0 0 1-4.893-1.471l-2.03.499zM12 1C6.477 1 2 5.477 2 11c0 1.821.439 3.538 1.215 5.057L2 22l6.071-1.59A10.976 10.976 0 0 0 12 21c5.523 0 10-4.477 10-10S17.523 1 12 1z"/>
  </svg>
);

const SCENARIOS = [
  {
    icon: AlertTriangle,
    situation: 'Lastiğim Patladı',
    desc: 'Acil tamir ekibimiz konumunuza geliyor. Yerinde müdahale, servise gitmenize gerek yok.',
    color: 'rgba(229,51,51,.12)',
    borderColor: 'rgba(229,51,51,.3)',
    iconColor: 'var(--red)',
    cta: 'Bu Durumda Ara',
  },
  {
    icon: CircleDot,
    situation: 'Stepnem Yok',
    desc: 'Stepneniz yoksa endişelenmeyin. Yeni lastik getirip yerinde takıyoruz.',
    color: 'rgba(74,143,245,.12)',
    borderColor: 'rgba(74,143,245,.3)',
    iconColor: 'var(--blue)',
    cta: 'Bu Durumda Ara',
  },
  {
    icon: Wind,
    situation: 'Sibobum Arızalı',
    desc: 'Sibop tamiri yerinde yapılıyor. Lastiğinizi tekrar şişirilmiş şekilde teslim ediyoruz.',
    color: 'rgba(16,185,129,.12)',
    borderColor: 'rgba(16,185,129,.3)',
    iconColor: 'var(--green)',
    cta: 'Bu Durumda Ara',
  },
  {
    icon: Settings,
    situation: 'Jantım Hasar Aldı',
    desc: 'Jant problemlerine anlık çözüm. Tüm jant çeşitleri için profesyonel müdahale.',
    color: 'rgba(255,175,50,.12)',
    borderColor: 'rgba(255,175,50,.3)',
    iconColor: '#ff9500',
    cta: 'Bu Durumda Ara',
  },
];

const STEPS = [
  { num: '01', title: 'Ara', desc: 'Tek tuşla bağlan. 7/24 görevli operatörümüz sizi karşılayacak.', icon: Phone },
  { num: '02', title: 'Konumunu Paylaş', desc: 'GPS konumunuzu gönderin veya adres tarif edin. Ekibimiz yola çıkıyor.', icon: MapPin },
  { num: '03', title: 'Biz Gelelim', desc: 'Ort. 30 dk içinde yanınızdayız. Tamir yerinde, aracın başında.', icon: Zap },
];

const VEHICLES = [
  'Binek otomobil', 'Kamyon', 'Minibüs', 'Otobüs',
  'Ticari araç / Taksi', 'Arazi aracı (4x4)', 'Motosiklet',
  'Traktör', 'Kamyonet', 'Bisiklet',
];

const TRUST_METRICS = [
  { icon: Zap, value: '30 dk', label: 'Ort. Müdahale', color: 'var(--red)' },
  { icon: Shield, value: '24 Ay', label: 'Garanti', color: 'var(--green)' },
  { icon: Phone, value: '3 Hat', label: 'Aktif Hat', color: 'var(--blue)' },
  { icon: Clock, value: '7/24', label: 'Kesintisiz', color: '#ff9500' },
];

export default function AcilYolYardim() {
  return (
    <main data-testid="acil-page" style={{ background: 'var(--bg)' }}>
      <SEOHead path="/acil-yol-yardim" />
      {/* ─── EMERGENCY HERO ─── */}
      <section
        data-testid="acil-hero"
        style={{
          background: 'var(--bg)',
          position: 'relative',
          overflow: 'hidden',
          paddingTop: 86,
        }}
      >
        {/* Red glow */}
        <div style={{
          position: 'absolute', top: '30%', left: '10%',
          width: 600, height: 500,
          background: 'radial-gradient(ellipse, rgba(229,51,51,.08) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 1, background: 'var(--border)' }} />

        <div className="wrap" style={{ position: 'relative', zIndex: 1, padding: '3.5rem 1.5rem 3.5rem' }}>
          {/* Breadcrumb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.78rem', color: 'var(--text2)', marginBottom: '1.25rem' }}>
            <a href="/" style={{ transition: 'color .2s' }}>Anasayfa</a>
            <span style={{ color: 'var(--muted)', fontSize: '.65rem' }}>/</span>
            <span style={{ color: 'var(--red)' }}>Acil Yol Yardım</span>
          </div>

          {/* Live badge + active teams */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '.75rem', marginBottom: '1.25rem' }}>
            <LiveBadge text="2 Ekip Şu An Sahada" />
            <span className="eyebrow" style={{ color: 'rgba(122,132,160,.5)' }}>Kocaeli Geneli</span>
          </div>

          {/* Headline */}
          <h1 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 800,
            fontSize: 'clamp(2.2rem, 6vw, 4rem)',
            color: 'var(--text)',
            lineHeight: 1,
            letterSpacing: '-.04em',
            marginBottom: '.5rem',
          }}>
            Lastiğin Patladı.
          </h1>
          <h1 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 800,
            fontSize: 'clamp(2.2rem, 6vw, 4rem)',
            color: 'var(--red)',
            lineHeight: 1,
            letterSpacing: '-.04em',
            marginBottom: '1.25rem',
          }}>
            Panik Yapma.
          </h1>
          <p style={{
            fontSize: 'clamp(.875rem, 1.5vw, 1rem)',
            color: 'var(--text2)',
            lineHeight: 1.72,
            maxWidth: 480,
            marginBottom: '2rem',
          }}>
            Konumunu paylaş — en yakın ekibimiz yola çıksın. Ortalama 30 dakikada, aracınızın başında.
          </p>

          {/* CTAs */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.75rem', marginBottom: '2rem' }}>
            <a
              href={PHONES.main_tel}
              data-testid="acil-hero-call"
              className="btn btn-primary btn-xl ring num"
              style={{ gap: '.625rem' }}
            >
              <Phone size={17} /> Hemen Ara — {PHONES.main}
            </a>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="acil-hero-whatsapp"
              data-gtm-action="whatsapp_click"
              className="btn btn-green"
              style={{ gap: '.5rem', padding: '.875rem 1.5rem', borderRadius: 'var(--rl)', fontSize: '.9375rem', fontWeight: 700 }}
            >
              {WA_ICON} WhatsApp ile Konum Gönder
            </a>
          </div>

          {/* Quick trust */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
            {['Yerinde tamir', '24 ay garanti', '12+ ilçe'].map(t => (
              <span key={t} style={{
                display: 'inline-flex', alignItems: 'center', gap: '.3rem',
                background: 'rgba(13,14,32,.7)', border: '1px solid rgba(28,31,60,.8)',
                borderRadius: 100, padding: '.25rem .75rem',
                fontSize: '.75rem', color: 'var(--text2)',
              }}>
                <span style={{ color: 'var(--green)', fontSize: '.65rem' }}>&#10003;</span> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TRUST METRICS BAR ─── */}
      <section data-testid="acil-trust-bar" style={{
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
      }}>
        <div className="wrap">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }} className="trust-grid">
            {TRUST_METRICS.map(({ icon: Icon, value, label, color }, i) => (
              <div
                key={label}
                style={{
                  padding: '1.75rem 1rem',
                  borderRight: i < 3 ? '1px solid var(--border)' : 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.875rem',
                }}
              >
                <div style={{
                  width: 42, height: 42, borderRadius: 11,
                  background: `${color}18`, border: `1px solid ${color}35`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: color, flexShrink: 0,
                }}>
                  <Icon size={18} strokeWidth={1.75} />
                </div>
                <div>
                  <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800, fontSize: '1.25rem', color: 'var(--text)', lineHeight: 1, letterSpacing: '-.03em' }}>{value}</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--text2)', marginTop: '.15rem' }}>{label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SCENARIO CARDS ─── */}
      <section className="section" style={{ background: 'var(--bg)' }}>
        <div className="wrap">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div className="pill pill-red" style={{ marginBottom: '1rem' }}>Durumunuzu Seçin</div>
            <h2 className="h2">
              Ne Oldu? <span style={{ color: 'var(--red)' }}>Biz Hazırız.</span>
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.25rem' }} className="scenario-grid">
            {SCENARIOS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.situation}
                  data-testid={`scenario-card-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: .5, delay: i * .08 }}
                  style={{
                    background: 'var(--card)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--rl)',
                    padding: '2rem',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'border-color .25s, transform .2s, box-shadow .25s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = s.borderColor;
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,.4)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Glow */}
                  <div style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    background: `radial-gradient(ellipse at top left, ${s.color} 0%, transparent 60%)`,
                    opacity: 0, transition: 'opacity .3s', pointerEvents: 'none',
                  }} className="scenario-glow" />

                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                      <div style={{
                        width: 48, height: 48, borderRadius: 12,
                        background: s.color, border: `1px solid ${s.borderColor}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: s.iconColor, flexShrink: 0,
                      }}>
                        <Icon size={22} strokeWidth={1.75} />
                      </div>
                      <h3 style={{
                        fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
                        fontSize: '1.15rem', color: 'var(--text)',
                      }}>
                        "{s.situation}"
                      </h3>
                    </div>
                    <p style={{ fontSize: '.875rem', color: 'var(--text2)', lineHeight: 1.7, marginBottom: '1.25rem' }}>
                      {s.desc}
                    </p>
                    <a
                      href={PHONES.main_tel}
                      className="btn btn-primary btn-sm num"
                      style={{ gap: '.4rem' }}
                    >
                      <Phone size={13} /> {s.cta}
                    </a>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section className="section" style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div className="wrap">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div className="pill" style={{ marginBottom: '1rem' }}>3 Adım</div>
            <h2 className="h2">
              Nasıl <span style={{ color: 'var(--blue)' }}>Çalışır?</span>
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }} className="steps-grid">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.num}
                  data-testid={`step-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: .4, delay: i * .12 }}
                  style={{
                    background: 'var(--card)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--rl)',
                    padding: '2rem',
                    textAlign: 'center',
                    position: 'relative',
                    transition: 'border-color .2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--blue-border)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <div style={{
                    fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800,
                    fontSize: '2.5rem', color: 'var(--border2)', lineHeight: 1,
                    marginBottom: '.75rem',
                  }}>{step.num}</div>
                  <div style={{
                    width: 48, height: 48, borderRadius: 12,
                    background: 'var(--blue-dim)', border: '1px solid var(--blue-border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'var(--blue)', margin: '0 auto .875rem',
                  }}>
                    <Icon size={20} />
                  </div>
                  <h3 style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: '1.05rem', color: 'var(--text)', marginBottom: '.5rem' }}>
                    {step.title}
                  </h3>
                  <p style={{ fontSize: '.85rem', color: 'var(--text2)', lineHeight: 1.65 }}>
                    {step.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── SERVICE AREA + VEHICLES ─── */}
      <section className="section" style={{ background: 'var(--bg)' }}>
        <div className="wrap">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', alignItems: 'start' }} className="area-grid">
            {/* Coverage Area */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: .5 }}>
              <div className="pill" style={{ marginBottom: '1rem' }}>Hizmet Bölgesi</div>
              <h2 style={{
                fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800,
                fontSize: 'clamp(1.4rem, 3vw, 2rem)', color: 'var(--text)',
                lineHeight: 1.15, letterSpacing: '-.025em', marginBottom: '.625rem',
              }}>
                Kocaeli'nin <span style={{ color: 'var(--blue)' }}>Her Noktasında</span>
              </h2>
              <p style={{ fontSize: '.9rem', color: 'var(--text2)', lineHeight: 1.7, marginBottom: '1.5rem' }}>
                12+ ilçe, 365 gün, 24 saat. Nerede olursanız olun, en yakın ekip yola çıkar.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
                {DISTRICT_PAGES.map(d => (
                  <Link key={d.slug} to={`/${d.slug}`} style={{
                    display: 'inline-block', background: 'var(--card)', border: '1px solid var(--border)',
                    borderRadius: 100, padding: '.3rem .85rem',
                    fontSize: '.8rem', color: 'var(--text2)',
                    transition: 'border-color .2s, color .2s', textDecoration: 'none',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--blue)'; e.currentTarget.style.color = 'var(--blue)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; }}
                  >{d.name}</Link>
                ))}
              </div>
            </motion.div>

            {/* Vehicle Types */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: .5, delay: .1 }}>
              <div className="pill pill-green" style={{ marginBottom: '1rem' }}>Araç Tipleri</div>
              <h2 style={{
                fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800,
                fontSize: 'clamp(1.4rem, 3vw, 2rem)', color: 'var(--text)',
                lineHeight: 1.15, letterSpacing: '-.025em', marginBottom: '.625rem',
              }}>
                Her Araç <span style={{ color: 'var(--green)' }}>Tipi İçin Hazırız</span>
              </h2>
              <p style={{ fontSize: '.9rem', color: 'var(--text2)', lineHeight: 1.7, marginBottom: '1.5rem' }}>
                Binek'ten kamyona, motosikletten traktöre — tüm araç lastiklerine hizmet veriyoruz.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.625rem' }}>
                {VEHICLES.map(v => (
                  <div key={v} style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                    <CheckCircle size={15} style={{ color: 'var(--green)', flexShrink: 0 }} />
                    <span style={{ fontSize: '.85rem', color: 'var(--text2)' }}>{v}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── IMPORTANT NOTE ─── */}
      <section style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', padding: '2.5rem 0' }}>
        <div className="wrap">
          <div style={{
            background: 'var(--card)', border: '1px solid var(--border)',
            borderLeft: '3px solid #f97316', borderRadius: 'var(--r)',
            padding: '1.25rem 1.5rem',
            display: 'flex', alignItems: 'flex-start', gap: '1rem',
            maxWidth: 640,
          }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10,
              background: 'rgba(249,115,22,.12)', border: '1px solid rgba(249,115,22,.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#f97316', flexShrink: 0,
            }}>
              <AlertTriangle size={18} />
            </div>
            <div>
              <div style={{ fontWeight: 700, color: '#f97316', marginBottom: '.25rem', fontSize: '.9rem' }}>
                Aklınızda Bulunsun
              </div>
              <p style={{ color: 'var(--text2)', fontSize: '.85rem', lineHeight: 1.65 }}>
                Tamirlerimiz aracınızın başında yapılır. Hiçbir yere götürülmez. Zaman kaybı yok, ek masraf yok.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <FinalCTA headline="Yolda Kalma." sub="Hemen Ara." />

      <style>{`
        @media(max-width:768px){
          .scenario-grid{grid-template-columns:1fr!important}
          .steps-grid{grid-template-columns:1fr!important}
          .area-grid{grid-template-columns:1fr!important}
          .trust-grid{grid-template-columns:repeat(2,1fr)!important}
        }
        @media(max-width:480px){
          .trust-grid{grid-template-columns:1fr!important}
          .trust-grid>div{border-right:none!important;border-bottom:1px solid var(--border)}
          .trust-grid>div:last-child{border-bottom:none}
        }
        .scenario-glow{opacity:0;transition:opacity .3s}
        [data-testid^="scenario-card"]:hover .scenario-glow{opacity:1!important}
      `}</style>
    </main>
  );
}
